import { useEffect, useState } from "react";
import { api, LOGO_URL } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BrandMarquee from "../components/BrandMarquee";
import ProductCard from "../components/ProductCard";
import { ArrowRight, Star } from "lucide-react";

export default function Landing() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState("all");

  const CATEGORIES = [
    { key: "all", label: t("cat_all") },
    { key: "hoodie", label: t("cat_hoodie") },
    { key: "tshirt", label: t("cat_tshirt") },
  ];

  useEffect(() => {
    api
      .get("/products")
      .then((r) => setProducts(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const featured = products.filter((p) => p.category === "hoodie");
  const CAT_ORDER = ["hoodie", "tshirt"];
  const sortByCat = (a, b) => {
    const ai = CAT_ORDER.indexOf(a.category);
    const bi = CAT_ORDER.indexOf(b.category);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  };
  const visible =
    activeCat === "all"
      ? [...products].sort(sortByCat)
      : products.filter((p) => p.category === activeCat);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />

      {/* HERO */}
      <section
        id="hero"
        data-testid="hero-section"
        className="relative border-b border-zinc-900 overflow-hidden"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-green-500/5 blur-[120px]" />
        </div>

        <div className="relative max-w-[1600px] mx-auto px-6 md:px-10 pt-20 pb-28 md:pt-32 md:pb-40 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-8 fade-up">
            <div className="flex items-center gap-3 mb-8 text-xs uppercase tracking-[0.3em] text-zinc-400">
              <span className="w-10 h-px bg-green-400" />
              <span>{t("drop_live")}</span>
            </div>

            <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-[16vw] md:text-[10vw] lg:text-[9vw]">
              ALL<br className="hidden md:block" />
              <span className="text-green-400">STAR</span>
            </h1>

            <p className="mt-8 max-w-xl text-lg text-zinc-400 leading-relaxed">
              {t("hero_subtitle")}
            </p>

            <div className="mt-12 flex flex-col sm:flex-row gap-4">
              <a
                href="#shop"
                data-testid="hero-shop-btn"
                className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-400 text-black font-bold px-8 py-5 uppercase tracking-widest text-sm transition-all duration-200 group"
              >
                {t("hero_shop")}
                <ArrowRight
                  size={18}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
              <a
                href="#story"
                data-testid="hero-story-btn"
                className="inline-flex items-center justify-center gap-3 bg-transparent border border-white hover:bg-white hover:text-black text-white font-bold px-8 py-5 uppercase tracking-widest text-sm transition-all duration-200"
              >
                {t("hero_story")}
              </a>
            </div>
          </div>

          <div className="md:col-span-4 flex items-center justify-center md:justify-end fade-up">
            <div className="relative w-full max-w-sm aspect-square border border-zinc-800 bg-black/80 backdrop-blur p-10 flex items-center justify-center">
              <img
                src={LOGO_URL}
                alt="ALLSTAR logo"
                className="w-full h-full object-contain"
                data-testid="hero-logo"
              />
              <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest text-zinc-500">
                {t("hero_est")}
              </div>
              <div className="absolute bottom-3 right-3 text-[10px] uppercase tracking-widest text-zinc-500">
                {t("hero_tagline")}
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee />

      {/* DROPS / FEATURED */}
      <section
        id="drops"
        data-testid="featured-section"
        className="max-w-[1600px] mx-auto px-6 md:px-10 py-24"
      >
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-3">
              {t("featured_kicker")}
            </p>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter">
              {t("featured_title")}
            </h2>
          </div>
          <a
            href="#shop"
            className="text-sm uppercase tracking-widest text-zinc-400 hover:text-green-400 flex items-center gap-2"
          >
            {t("view_all")} <ArrowRight size={16} />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading &&
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className="aspect-[4/5] border border-zinc-900 bg-zinc-950 animate-pulse"
              />
            ))}
          {!loading &&
            featured
              .slice(0, 4)
              .map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* STORY */}
      <section
        id="story"
        data-testid="story-section"
        className="border-y border-zinc-900 bg-zinc-950/50"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-3">
              {t("story_kicker")}
            </p>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-none">
              {t("story_title_1")}
              <br />
              {t("story_title_2")}
              <br />
              <span className="text-green-400">{t("story_title_3")}</span>
            </h2>
          </div>
          <div className="md:col-span-7 md:pl-12 border-l border-zinc-900">
            <p className="text-lg text-zinc-300 leading-relaxed">
              {t("story_body")}
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              {[
                { k: "01", v: t("stat_1") },
                { k: "02", v: t("stat_2") },
                { k: "03", v: t("stat_3") },
              ].map((s) => (
                <div key={s.k} className="border-t border-zinc-800 pt-4">
                  <p className="font-display text-3xl text-green-400">{s.k}</p>
                  <p className="text-xs uppercase tracking-widest text-zinc-400 mt-2">
                    {s.v}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ALL PRODUCTS */}
      <section
        id="shop"
        data-testid="shop-section"
        className="max-w-[1600px] mx-auto px-6 md:px-10 py-24"
      >
        <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-green-400 mb-3">
              {t("shop_kicker")}
            </p>
            <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter flex items-center gap-4">
              {t("shop_title")}{" "}
              <Star
                className="text-green-400"
                size={48}
                fill="currentColor"
              />
            </h2>
          </div>
          <p className="text-zinc-500 text-sm uppercase tracking-widest">
            {t("pieces_live", visible.length)}
          </p>
        </div>

        {/* Category filter */}
        <div
          data-testid="category-filter"
          className="flex flex-wrap gap-3 mb-10 border-y border-zinc-900 py-5"
        >
          {CATEGORIES.map((c) => {
            const count =
              c.key === "all"
                ? products.length
                : products.filter((p) => p.category === c.key).length;
            const active = activeCat === c.key;
            return (
              <button
                key={c.key}
                data-testid={`filter-${c.key}`}
                onClick={() => setActiveCat(c.key)}
                className={`text-xs uppercase tracking-widest px-5 py-3 border transition-all duration-200 ${
                  active
                    ? "bg-green-500 text-black border-green-500 font-bold"
                    : "bg-transparent text-zinc-400 border-zinc-800 hover:border-green-400 hover:text-white"
                }`}
              >
                {c.label}{" "}
                <span
                  className={`ml-1 ${
                    active ? "text-black/60" : "text-zinc-600"
                  }`}
                >
                  ({count})
                </span>
              </button>
            );
          })}
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          data-testid="shop-grid"
        >
          {loading &&
            [0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className="aspect-[4/5] border border-zinc-900 bg-zinc-950 animate-pulse"
              />
            ))}
          {!loading &&
            visible.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          {!loading && visible.length === 0 && (
            <div className="col-span-full border border-zinc-900 p-16 text-center">
              <p className="text-zinc-500 uppercase tracking-widest text-sm">
                {products.length === 0 ? t("empty_all") : t("empty_drop")}
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
